import React from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import Title from 'ui/Typography/Title';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import Body from 'ui/Typography/Body';
import {
  KompisImage,
  KompisSheetBody,
  KompisSheetBodyPadding,
  KompisSheetFooter,
  KompisSheetFooterButtons
} from '@inter-ikea-kompis/react-components';
import Sheet from 'ui/Sheet/Sheet';
import SheetHeader from 'ui/Sheet/SheetHeader';
import Box from 'ui/Box';
import handlesImage from './handlesForAllFronts.jpeg';
import pushOpenImage from './pushOpenForAllFronts.jpeg';
import { ProductType } from '@inter-ikea-storageone/range';
import {
  changeToPushOpenFunctionAndRemoveHandles,
  changeToSoftClosingFunctionAndAddHandles
} from 'rangeConfig/besta/utils/doorClosingFunction';
import { useCore } from 'contexts/core';
import { reportFrontsOpeningFunctionSheetChoice } from 'analytics/reporters';
import { useAppDispatch } from 'ducks/hooks';
import { getMarketProducts } from 'ducks/products';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import {
  isModalVisible,
  hideFrontsOpeningFunctionSheet,
  getHandleId
} from './FrontsOpeningFunctionFeature';

function FrontsOpeningFunctionSheet() {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const visible = !!useSelector(isModalVisible);
  const handleId = useSelector(getHandleId) ?? '';
  const products = useSelector(getMarketProducts);
  const coreApi = useCore();

  const onBack = () => {
    dispatch(hideFrontsOpeningFunctionSheet());
  };

  const onClose = () => {
    dispatch(hideFrontsOpeningFunctionSheet());
  };

  const onSelect = () => {
    if (handleId === ProductType.BESTA_PUSH_OPEN) {
      changeToPushOpenFunctionAndRemoveHandles(coreApi, products);
    } else {
      changeToSoftClosingFunctionAndAddHandles(handleId, coreApi, products);
    }

    onClose();
  };

  const title =
    handleId === ProductType.BESTA_PUSH_OPEN
      ? 'storageone.usePushOpenAllFrontsSheetTitle'
      : 'storageone.useHandlesAllFrontsSheetTitle';

  const description =
    handleId === ProductType.BESTA_PUSH_OPEN
      ? 'storageone.usePushOpenAllFrontsSheetBody'
      : 'storageone.useHandlesAllFrontsSheetBody';

  const image = handleId === ProductType.BESTA_PUSH_OPEN ? pushOpenImage : handlesImage;

  const buttonText =
    handleId === ProductType.BESTA_PUSH_OPEN
      ? 'storageone.usePushOpenAllFrontsSheetButton'
      : 'storageone.useHandlesAllFrontsSheetButton';

  return (
    <Sheet onOverlayClick={onClose} visible={visible}>
      <SheetHeader backIcon={true} onBackButtonClick={onBack} onCloseButtonClick={onClose} />
      <KompisSheetBody theme={SkapaTheme}>
        <KompisSheetBodyPadding theme={SkapaTheme}>
          <Title type={ThemeFontStyleTypeEnum.headingMedium}>{formatMessage({ id: title })}</Title>
          <Body mt="l" type={ThemeFontStyleTypeEnum.bodyLarge}>
            {formatMessage({ id: description })}
          </Body>
          <Box mt="xl">
            <KompisImage src={image} alt={formatMessage({ id: title })} />
          </Box>
        </KompisSheetBodyPadding>
      </KompisSheetBody>
      <KompisSheetFooter theme={SkapaTheme}>
        <KompisSheetFooterButtons
          theme={SkapaTheme}
          primaryLabel={formatMessage({
            id: buttonText
          })}
          secondaryLabel={formatMessage({ id: 'kompis.buttonCancel' })}
          onButtonClick={event => {
            if (event.detail.type === 'primaryButton') {
              reportFrontsOpeningFunctionSheetChoice({ choice: 'all' });
              onSelect();
            } else {
              reportFrontsOpeningFunctionSheetChoice({ choice: 'dismiss' });
              onClose();
            }
          }}
        />
      </KompisSheetFooter>
    </Sheet>
  );
}

export default FrontsOpeningFunctionSheet;
